<script setup lang="ts">
import { CtButton } from '@camelot/components'
import { useIntersectionObserver } from '@vueuse/core'
import SellingArgumentsSlide from '~/components/banner/info/SellingArgumentsSlide.vue'
import SellingArgumentsText from '~/components/banner/info/SellingArgumentsText.vue'
import { translate } from '~/composables/translate/translations'
import { useReferralStore } from '~/stores/referral'

const referralStore = useReferralStore()
const metaFlowOffersEnabled = useRuntimeConfig().public.META_OFFERS_ENABLED && referralStore.isMetaFlow

const store = useReferralStore()

const pickUpImg = `${useRuntimeConfig().public.imageURL}/selling-args/pick-up.svg`
const fuelImg = `${useRuntimeConfig().public.imageURL}/selling-args/fuel.svg`
const keylessImg = `${useRuntimeConfig().public.imageURL}/selling-args/keyless.svg`

const pickUpArgs = ref<HTMLLIElement>()
const keyLessArgs = ref<HTMLLIElement>()
const fuelArgs = ref<HTMLLIElement>()

const pickUpArgsVisible = ref<boolean>(false)
const keyLessArgsVisible = ref<boolean>(false)
const fuelArgsVisible = ref<boolean>(false)

const showMoreInformation = ref(false)

const options = { threshold: 1 }

const { stop: stopSlide1 } = useIntersectionObserver(
  pickUpArgs,
  ([{ isIntersecting }]) => pickUpArgsVisible.value = isIntersecting,
  options,
)
const { stop: stopSlide2 } = useIntersectionObserver(
  fuelArgs,
  ([{ isIntersecting }]) => fuelArgsVisible.value = isIntersecting,
  options,
)
const { stop: stopSlide3 } = useIntersectionObserver(
  keyLessArgs,
  ([{ isIntersecting }]) => keyLessArgsVisible.value = isIntersecting,
  options,
)

const scrollingOptions: ScrollIntoViewOptions = { behavior: 'smooth', block: 'nearest' }
function scrollInto(args: 'pickUpArgs' | 'fuelArgs' | 'keyLessArgs') {
  switch (args) {
    case 'pickUpArgs': pickUpArgs.value?.scrollIntoView(scrollingOptions); break
    case 'fuelArgs': fuelArgs.value?.scrollIntoView(scrollingOptions); break
    case 'keyLessArgs': keyLessArgs.value?.scrollIntoView(scrollingOptions); break
  }
}

function getButtonBackgroundColor(isVisible: boolean) {
  return isVisible ? 'bg-black-80' : 'bg-black-20'
}

onBeforeUnmount(() => { stopSlide1(); stopSlide2(); stopSlide3() })
</script>

<!--
TODO: Adjust this back to https://gitlab.ridedev.io/telematics/zuul/web/booking-ui/-/commit/47b847ebb434f22cd9b63b69936426d632f14008
      once meta offers are being used. Also look at comment in @Referral.vue TVK-1625
-->
<template>
  <section class="text-black-100 bg-mint-10 p-16 rounded-xl flex flex-col space-y-8">
    <article class="w-full lg:-mb-24">
      <h4 v-if="store.isFromReferral">
        {{ translate('selling_proposition_prebooking_meta_subtitle') }}
      </h4>
      <ul class="overflow-x-auto scrollbar-hide snap-mandatory snap-x flex flex-row gap-24 text-black-100
        md:grid md:overflow-x-visible md:gap-60" :class="[metaFlowOffersEnabled ? 'md:grid-cols-2' : 'md:grid-cols-3']">
        <li ref="pickUpArgs" class="xs:pl-8 lg:pl-0">
          <SellingArgumentsSlide :src="pickUpImg" :caption="translate('selling_proposition_prebooking_pick_up')"
            img-alt="Locate the vehicle and just drive" />
        </li>
        <li ref="fuelArgs" v-if="!metaFlowOffersEnabled">
          <SellingArgumentsSlide :src="fuelImg" :caption="translate('selling_proposition_prebooking_fueling')"
            img-alt="Fuel costs are on us" />
        </li>
        <li ref="keyLessArgs">
          <SellingArgumentsSlide :src="keylessImg" :caption="translate('selling_proposition_prebooking_keyless')"
            img-alt="This car is keyless" />
        </li>
      </ul>
      <footer class="flex flex-row gap-16 mx-auto justify-center md:hidden">
        <button type="button" class="w-8 h-8 rounded-full" :class="getButtonBackgroundColor(pickUpArgsVisible)"
          @click="scrollInto('pickUpArgs')" />
        <button type="button" class="w-8 h-8 rounded-full" :class="getButtonBackgroundColor(fuelArgsVisible)"
          @click="scrollInto('fuelArgs')" />
        <button type="button" class="w-8 h-8 rounded-full" :class="getButtonBackgroundColor(keyLessArgsVisible)"
          @click="scrollInto('keyLessArgs')" />
      </footer>
    </article>

    <CtButton variant="text" type="button" size="sm" :suffix-icon="showMoreInformation ? 'chevron_up' : 'chevron_down'"
      class="info-button mt-0 pt-0 bg-mint-10" data-testid="more-info-button"
      @click="showMoreInformation = !showMoreInformation">
      <span class="text-base">
        {{ showMoreInformation ? translate("selling_proposition_prebooking_less_information") :
          translate("selling_proposition_prebooking_more_information") }}
      </span>
    </CtButton>

    <article v-show="showMoreInformation" data-testid="more-info-section">
      <ul class="text-black-100 grid grid-cols-1 gap-24 lg:gap-60"
        :class="[metaFlowOffersEnabled ? 'lg:grid-cols-2' : 'lg:grid-cols-3']">
        <SellingArgumentsText :title="translate('selling_proposition_prebooking_dropoff_subtitle')"
          :text="translate('selling_proposition_prebooking_dropoff')" icon="location" />
        <SellingArgumentsText v-if="!metaFlowOffersEnabled"
          :title="translate('selling_proposition_prebooking_package_subtitle')"
          :text="translate('selling_proposition_prebooking_package')" icon="gas_station" />
        <SellingArgumentsText :title="translate('selling_proposition_prebooking_instantaccess_subtitle')"
          :text="translate('selling_proposition_prebooking_instantaccess')" icon="key_unlock" />
      </ul>
    </article>
  </section>
</template>

<style lang="scss" scoped>
li {
  @apply shrink-0 basis-3/4 snap-start;
}

.info-button {
  background-color: transparent;
  border-style: none !important;
}
</style>
